import * as React from 'react';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { ChevronDown, ChevronRight, Plus, Minus, X } from 'lucide-react';
import { cva, type VariantProps } from 'class-variance-authority';

const accordionHeaderVariants = cva('rocco-accordion-header flex', {
    variants: {
        variant: {
            default: '',
            spacious: '',
            narrow: '',
        },
    },
    defaultVariants: {
        variant: 'default',
    },
});

const accordionTriggerVariants = cva(
    'rocco-accordion-trigger flex flex-1 items-center justify-between text-sm transition-all [&[data-state=open]>svg]:rotate-180',
    {
        variants: {
            variant: {
                narrow: 'py-3 font-medium',
                default: 'py-4 font-medium',
                spacious: 'py-5 font-medium md:data-[state=open]:pb-0',
            },
        },
        defaultVariants: {
            variant: 'default',
        },
    }
);

const accordionIconVariants = cva(
    'rocco-accordion-icon shrink-0 transition-transform duration-200',
    {
        variants: {
            variant: {
                default: 'h-4 w-4',
                spacious: 'h-5 w-5',
                narrow: 'h-4 w-4',
            },
            startDirection: {
                down: '',
                right: 'rotate-[-90deg]',
            },
        },
        defaultVariants: {
            variant: 'default',
            startDirection: 'down',
        },
    }
);

const ICON_COMPONENTS = {
    chevronDown: ChevronDown,
    chevronRight: ChevronRight,
    plus: Plus,
    minus: Minus,
    x: X,
} as const;

type IconType = keyof typeof ICON_COMPONENTS;

export const AccordionTrigger = React.forwardRef<
    React.ElementRef<typeof AccordionPrimitive.Trigger>,
    React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger> &
        VariantProps<typeof accordionTriggerVariants> &
        VariantProps<typeof accordionIconVariants> & {
            icon?: IconType;
            openIcon?: IconType;
            isOpen?: boolean;
        }
>(
    (
        {
            className,
            children,
            variant,
            startDirection,
            icon = 'chevronDown',
            openIcon,
            isOpen = false,
            ...props
        },
        ref
    ) => {
        return (
            <AccordionPrimitive.Header
                className={accordionHeaderVariants({ variant })}
            >
                <AccordionPrimitive.Trigger
                    ref={ref}
                    className={
                        accordionTriggerVariants({ variant, className }) +
                        ' group'
                    }
                    {...props}
                >
                    {children}
                    {openIcon ? (
                        <div className="relative">
                            {/* Default icon - shown when closed */}
                            <div style={{ display: isOpen ? 'block' : 'none' }}>
                                {React.createElement(ICON_COMPONENTS[icon], {
                                    className: accordionIconVariants({
                                        startDirection: startDirection,
                                        variant: variant,
                                    }),
                                })}
                            </div>
                            {/* Open icon - shown when open */}
                            <div style={{ display: isOpen ? 'none' : 'block' }}>
                                {React.createElement(
                                    ICON_COMPONENTS[openIcon],
                                    {
                                        className: accordionIconVariants({
                                            startDirection: startDirection,
                                            variant: variant,
                                        }),
                                    }
                                )}
                            </div>
                        </div>
                    ) : (
                        /* Show single icon with rotation when no openIcon is provided */
                        React.createElement(ICON_COMPONENTS[icon], {
                            className: accordionIconVariants({
                                startDirection: startDirection,
                                variant: variant,
                            }),
                        })
                    )}
                </AccordionPrimitive.Trigger>
            </AccordionPrimitive.Header>
        );
    }
);

AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;
